import { useEffect, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { getSoundSettings, onSoundSettings, setSoundSettings } from '../audio/sounds.js';
import { onRadio, openRadioPopup, radioConfig, radioPopupSupported, radioStatus } from '../audio/radio.js';

/** iOS ignores the volume of media elements; the device buttons set it. */
const isIos = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/**
 * Radio player in the lobby (decision 2026-09-29) for players with a music permission: listen /
 * stop, mute, volume, the state of the broadcast and a separate small radio window that keeps
 * playing when Areena is closed. The radio keeps playing on the other pages.
 */
export function RadioPlayer(props: { compact?: boolean } = {}) {
  const [s, setS] = useState(getSoundSettings());
  useEffect(() => onSoundSettings(setS), []);
  const [, setTick] = useState(0);
  useEffect(() => onRadio(() => setTick((n) => n + 1)), []);
  const config = radioConfig();
  if (!config) return null;
  const status = radioStatus();
  const popup = status === 'popup';
  const r = t.radio;
  return (
    <section class={`${props.compact ? 'radio-compact' : 'card'} radio-player radio-${status}`}>
      <div class="radio-head">
        <span class="radio-icon" aria-hidden="true">
          📻
        </span>
        <div class="radio-title">
          <strong>{config.name}</strong>
          <span class="radio-status" role="status">
            {status === 'playing' && <span class="radio-live" aria-hidden="true" />}
            {status === 'playing' && s.musicMuted ? r.mutedStatus : r.status[status]}
          </span>
        </div>
        {!popup && (
          <button class={`btn btn-small ${s.music ? '' : 'btn-primary'}`} onClick={() => setSoundSettings({ ...s, music: !s.music })}>
            {s.music ? `⏹ ${r.stop}` : `▶ ${r.play}`}
          </button>
        )}
      </div>
      {!popup && (
        <div class="radio-controls">
          <button
            class={`btn btn-small btn-icon-text ${s.musicMuted ? 'btn-primary' : ''}`}
            onClick={() => setSoundSettings({ ...s, musicMuted: !s.musicMuted })}
            aria-pressed={s.musicMuted}
            title={s.musicMuted ? r.unmute : r.mute}
          >
            {s.musicMuted ? `🔇 ${r.unmute}` : `🔊 ${r.mute}`}
          </button>
          {isIos ? (
            <span class="hint">{r.iosVolume}</span>
          ) : (
            <label class="radio-volume">
              <span class="visually-hidden">{r.volume}</span>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(s.musicVolume * 100)}
                disabled={s.musicMuted}
                aria-label={r.volume}
                onInput={(e) => setSoundSettings({ ...s, musicVolume: Number(e.currentTarget.value) / 100 })}
              />
              <span class="radio-percent">{Math.round(s.musicVolume * 100)} %</span>
            </label>
          )}
        </div>
      )}
      {radioPopupSupported && !popup && (
        <button class="btn btn-ghost btn-small radio-popup-btn" onClick={openRadioPopup} title={r.popupTitle}>
          ↗ {r.popup}
        </button>
      )}
    </section>
  );
}
